import type { RebalanceRow } from "../../rebalances/lib";
import type { RQLFieldDefinition } from "./types";

export const REBALANCE_STATUS_VALUES = [
  "requested",
  "pending",
  "proposed",
  "approved",
  "executing",
  "complete",
  "failed",
  "canceled",
] as const;

export const REBALANCE_TYPE_VALUES = [
  "tolerance",
  "raise_cash",
  "spend_cash",
  "tlh",
  "manual",
  "position_swap",
] as const;

export const RQL_FIELD_DEFINITIONS: Record<string, RQLFieldDefinition> = {
  unsettled_cash_total: {
    name: "unsettled_cash_total",
    type: "number",
    accessor: (row: RebalanceRow) => row.cashBalance ?? 0,
  },
  num_trades: {
    name: "num_trades",
    // APP-1279: the old accessor counted account sub-rows (always 0/wrong); use
    // the trade count surfaced from the blob.
    type: "number",
    accessor: (row: RebalanceRow) => row.numTrades ?? 0,
  },
  status: {
    name: "status",
    type: "string",
    accessor: (row: RebalanceRow) => row.status,
    allowedValues: REBALANCE_STATUS_VALUES,
  },
  type: {
    name: "type",
    // APP-1279: filter by rebalance type (Tolerance/TLH/Cash/etc).
    type: "string",
    accessor: (row: RebalanceRow) => row.type ?? "",
    allowedValues: REBALANCE_TYPE_VALUES,
  },
  rebalance_reason: {
    name: "rebalance_reason",
    // APP-1280: filter by the breached band (e.g. "Risk Allocation").
    type: "string",
    accessor: (row: RebalanceRow) => row.reason ?? "",
  },
  household_name: {
    name: "household_name",
    type: "string",
    accessor: (row: RebalanceRow) =>
      row.level === "household" ? row.name : "",
  },
  aum_total: {
    name: "aum_total",
    type: "number",
    accessor: (row: RebalanceRow) => row.totalBalance ?? 0,
  },
  buy_total: {
    name: "buy_total",
    type: "number",
    accessor: (row: RebalanceRow) => row.buyTotal ?? 0,
  },
  sell_total: {
    name: "sell_total",
    type: "number",
    accessor: (row: RebalanceRow) => row.sellTotal ?? 0,
  },
  realized_gain_loss_total: {
    name: "realized_gain_loss_total",
    type: "number",
    accessor: (row: RebalanceRow) =>
      (row.realizedGainLossSt ?? 0) + (row.realizedGainLossLt ?? 0),
  },
} as const;

export const ALLOWED_FIELD_NAMES = Object.keys(
  RQL_FIELD_DEFINITIONS,
) as readonly string[];

export const ALLOWED_OPERATORS = [
  "=",
  "!=",
  ">",
  ">=",
  "<",
  "<=",
  "and",
  "or",
  "(",
  ")",
] as const;
